import { type ReactElement, useEffect, useState } from 'react';
import { type FoodMerchantView, type FoodMerchantLocationView, type MerchantMembershipView } from '@vello/api-client';
import { COMPANY_SUSPEND_REASONS } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { useScopeZone } from '../components/scope-zone';
import { formatMarketInstant as fmtZoned, mayManageStoreTeam, storeOnboardingActions } from '../../src/index';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, PanelHead, Row, Text, Ink, Stack, EmptyCell } from '@vello/web-theme';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { PartnerAccountsPanel } from '../components/partner-accounts-panel';
import { FleetsPanel } from '../components/fleets-panel';
import { StoreResponsibleInvite } from '../components/store-responsible-invite';
import { StoreLiveDrawer } from '../components/store-live-drawer';

/**
 * Companies (Empresas) — the platform-operator oversight of registered businesses. This first vertical is the
 * marketplace **merchants** (real, operator-gated): list every merchant, then drill into one to see its staff
 * logins, its store locations, and a note for its menu (the merchant→products index is a tracked follow-up).
 * Fleet organizations (D849: create one, give it an owner, link its drivers) live in the Frotas panel. Merchant display names resolve from the legal-
 * entity registry (a later join) — until then the legal-entity reference is shown, never a fabricated name.
 */
const STATUS_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  active: 'success',
  suspended: 'warning',
  rejected: 'danger',
  in_review: 'neutral',
  draft: 'neutral',
};

export function CompaniesScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t } = useT();
  const { fail, v, explain: said } = useVocab();
  const countryName = useCountryName();
  // D446 (M-12, M-11): put a store live (the checklist drawer) and manage a store's team from here.
  const onboarding = storeOnboardingActions(grants);
  const mayTeam = mayManageStoreTeam(grants);
  const [storeLive, setStoreLive] = useState<{ open: boolean; merchant?: FoodMerchantView }>({ open: false });
  const [invite, setInvite] = useState<{ identifier: string; role: 'owner' | 'manager' | 'staff' }>({ identifier: '', role: 'staff' });
  const { zone, lang } = useScopeZone(); // D420: dates in the scope's market zone (A-18)
  const { confirm } = useConsole();
  const [merchants, setMerchants] = useState<readonly FoodMerchantView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<readonly FoodMerchantLocationView[]>([]);
  const [staff, setStaff] = useState<readonly MerchantMembershipView[]>([]);
  const [detailError, setDetailError] = useState<string | undefined>(undefined);
  const [refresh, setRefresh] = useState(0);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void runtime.foodMerchant.listMerchants().then((res) => {
      if (cancelled) return;
      if (res.ok) setMerchants(res.value.merchants);
      else {
        setMerchants([]);
        setError(t('companies.error.list', { code: fail(res.error) }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, t, refresh]);

  // D401: a suspension carries the reason the operator PICKS (the server keeps it as the merchant's
  // `statusReasonCode`). Before this every suspension sent `operator_suspension`, which says who did it and
  // nothing about why. A reactivation is the undo of a known state and keeps its single code.
  const changeMerchantStatus = async (merchantId: string, action: 'suspend' | 'reactivate'): Promise<void> => {
    setMsg(undefined);
    let reasonCode = 'operator_reactivation';
    if (action === 'suspend') {
      const answer = await confirm({
        title: t('companies.suspend.confirm.title'),
        body: t('companies.suspend.confirm.body'),
        confirmLabel: t('companies.suspend'),
        cancelLabel: t('ui.cancel'),
        tone: 'danger',
        icon: 'pause',
        reasons: COMPANY_SUSPEND_REASONS.map((code) => v('business.company_suspend_reason', code)),
        reasonLabel: t('w6.reason.label'),
        reasonsOnly: true,
      });
      if (!answer.confirmed || answer.reason === undefined) return;
      const picked = COMPANY_SUSPEND_REASONS.find((code) => v('business.company_suspend_reason', code) === answer.reason);
      if (picked === undefined) return;
      reasonCode = picked;
    }
    const res = action === 'suspend' ? await runtime.foodMerchant.suspend(merchantId, reasonCode) : await runtime.foodMerchant.reactivate(merchantId, reasonCode);
    if (res.ok) {
      setMsg({ tone: 'success', text: t(action === 'suspend' ? 'companies.suspended' : 'companies.reactivated') });
      setRefresh((n) => n + 1);
    } else {
      setMsg({ tone: 'danger', text: t('companies.actionFailed', { code: fail(res.error) }) });
    }
  };

  const openMerchant = async (merchantId: string): Promise<void> => {
    setSelected(merchantId);
    setDetailError(undefined);
    setLocations([]);
    setStaff([]);
    const [loc, mem] = await Promise.all([runtime.foodMerchant.listLocations(merchantId), runtime.merchantConsole.listMemberships(merchantId)]);
    if (loc.ok) setLocations(loc.value.locations);
    else setDetailError(t('companies.error.detail', { code: fail(loc.error) }));
    if (mem.ok) setStaff(mem.value.memberships);
    else setDetailError(t('companies.error.detail', { code: fail(mem.error) }));
  };

  // D446 (M-11): the store team — invite by the person's MERCHANT sign-in, remove with the grant (server side).
  const explainTeam = (e: { readonly code: string; readonly reasonCode?: string }): string => said(e) ?? t('pob.refused', { code: fail(e) });
  const inviteStaff = async (merchantId: string): Promise<void> => {
    setMsg(undefined);
    const res = await runtime.merchantConsole.grantMembership({ identifier: invite.identifier.trim(), merchantId, role: invite.role });
    if (res.ok) {
      setInvite({ identifier: '', role: 'staff' });
      setMsg({ tone: 'success', text: t('pob.team.done') });
      await openMerchant(merchantId);
    } else setMsg({ tone: 'danger', text: explainTeam(res.error) });
  };
  const revokeStaff = async (merchantId: string, membershipId: string): Promise<void> => {
    const answer = await confirm({ title: t('pob.team.revoke'), body: t('pob.team.revoke.body'), confirmLabel: t('pob.team.revoke'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    const res = await runtime.merchantConsole.revokeMembership(membershipId);
    if (res.ok) {
      setMsg({ tone: 'success', text: t('pob.team.done') });
      await openMerchant(merchantId);
    } else setMsg({ tone: 'danger', text: explainTeam(res.error) });
  };

  const selectedMerchant = merchants?.find((m) => m.merchantId === selected);
  const activeStaff = staff.filter((s) => s.revokedAt === undefined);

  return (
    <>
      <PageHeader
        title={t('companies.title')}
        subtitle={t('companies.subtitle')}
        actions={onboarding.mayOnboard ? <Button variant="primary" onClick={() => setStoreLive({ open: true })}>{t('pob.store.open')}</Button> : undefined}
      />

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <Panel padded={false}>
        <PanelHead title={t('companies.merchants')} />
        <Table
          columns={[
            { key: 'entity', header: t('companies.col.merchant'), render: (m: FoodMerchantView) => m.legalEntityRef },
            { key: 'status', header: t('companies.col.status'), render: (m: FoodMerchantView) => <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{v('food.merchant_status', m.status)}</Badge> },
            { key: 'country', header: t('companies.col.country'), render: (m: FoodMerchantView) => countryName(m.scope.countryCode) },
            {
              key: 'act',
              header: '',
              render: (m: FoodMerchantView) => (
                <Row gap={2} align="stretch" justify="end">
                  {onboarding.mayOnboard && (m.status === 'draft' || m.status === 'in_review') && (
                    <Button variant="ghost" onClick={() => setStoreLive({ open: true, merchant: m })}>{t('pob.store.continue')}</Button>
                  )}
                </Row>
              ),
            },
          ]}
          rows={merchants ?? []}
          rowKey={(m) => m.merchantId}
          onRowClick={(m) => void openMerchant(m.merchantId)}
          selectedKey={selected}
          empty={<EmptyCell>{merchants === undefined ? t('companies.loading') : t('companies.empty')}</EmptyCell>}
        />
      </Panel>

      <FleetsPanel />

      <PartnerAccountsPanel />

      <Drawer open={selectedMerchant !== undefined} onClose={() => setSelected(undefined)} labelledBy="companies-detail-title">
        {selectedMerchant !== undefined && (
          <>
            <DrawerHeader titleId="companies-detail-title" title={selectedMerchant.legalEntityRef} subtitle={<Badge tone={STATUS_TONE[selectedMerchant.status] ?? 'neutral'}>{v('food.merchant_status', selectedMerchant.status)}</Badge>} onClose={() => setSelected(undefined)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack gap={6}>
                <IdRef value={selectedMerchant.merchantId} copy full />
                {msg !== undefined && (
                  <div>
                    <Notice tone={msg.tone}>{msg.text}</Notice>
                  </div>
                )}

                {detailError !== undefined && (
                  <div>
                    <Notice tone="danger">{detailError}</Notice>
                  </div>
                )}

                <Text as="h3" role="title">{t('companies.staff')}</Text>
                <Stack gap={3}>
                  <Table
                    columns={[
                      { key: 'user', header: t('companies.col.user'), render: (s: MerchantMembershipView) => (s.identifier !== undefined ? <span className="tnum">{s.identifier}</span> : <IdRef value={s.userId} />) },
                      { key: 'role', header: t('companies.col.role'), render: (s: MerchantMembershipView) => <Badge tone="neutral">{v('merchant.role', s.role)}</Badge> },
                      { key: 'since', header: t('companies.col.since'), render: (s: MerchantMembershipView) => <span className="tnum">{fmtZoned(s.grantedAt, zone, lang, 'date')}</span> },
                      ...(mayTeam ? [{ key: 'revoke', header: '', render: (s: MerchantMembershipView) => <Ink as="div" align="end" block><Button variant="ghost" onClick={() => void revokeStaff(s.merchantId, s.membershipId)}>{t('pob.team.revoke')}</Button></Ink> }] : []),
                    ]}
                    rows={activeStaff}
                    rowKey={(s) => s.membershipId}
                    empty={<EmptyCell>{t('companies.staff.empty')}</EmptyCell>}
                  />
                  {mayTeam && (
                    <FormRow
                      action={
                        <Button variant="secondary" disabled={invite.identifier.trim() === ''} onClick={() => void inviteStaff(selectedMerchant.merchantId)}>{t('pob.team.invite')}</Button>
                      }
                    >
                      <Field label={t('pob.team.identifier')} htmlFor="team-identifier"><TextInput id="team-identifier" type="email" value={invite.identifier} onChange={(e) => setInvite({ ...invite, identifier: e.target.value })} /></Field>
                      <Field label={t('pob.member.role')} htmlFor="team-role">
                        <Select id="team-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as 'owner' | 'manager' | 'staff' })}>
                          {(['owner', 'manager', 'staff'] as const).map((r) => <option key={r} value={r}>{v('merchant.role', r)}</option>)}
                        </Select>
                      </Field>
                    </FormRow>
                  )}
                </Stack>

                {mayTeam && (
                  <>
                    <Text as="h3" role="title">{t('pob.owner.invite')}</Text>
                    <StoreResponsibleInvite merchantId={selectedMerchant.merchantId} countryCode={selectedMerchant.scope.countryCode} onDone={() => void openMerchant(selectedMerchant.merchantId)} />
                  </>
                )}

                <Text as="h3" role="title">{t('companies.locations')}</Text>
                <div>
                  <Table
                    columns={[
                      { key: 'loc', header: t('companies.col.location'), render: (l: FoodMerchantLocationView) => <IdRef value={l.locationId} /> },
                      { key: 'status', header: t('companies.col.status'), render: (l: FoodMerchantLocationView) => <Badge tone={l.status === 'active' ? 'success' : 'warning'}>{v('food.location_status', l.status)}</Badge> },
                    ]}
                    rows={locations}
                    rowKey={(l) => l.locationId}
                    empty={<EmptyCell>{t('companies.locations.empty')}</EmptyCell>}
                  />
                </div>

                <Text as="h3" role="title">{t('companies.products')}</Text>
                <Notice tone="neutral">{t('companies.products.pending')}</Notice>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setSelected(undefined)}>{t('ui.close')}</Button>
              {selectedMerchant.status === 'active' && (
                <Button variant="danger" onClick={() => void changeMerchantStatus(selectedMerchant.merchantId, 'suspend')}>{t('companies.suspend')}</Button>
              )}
              {selectedMerchant.status === 'suspended' && (
                <Button variant="secondary" onClick={() => void changeMerchantStatus(selectedMerchant.merchantId, 'reactivate')}>{t('companies.reactivate')}</Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
      <StoreLiveDrawer open={storeLive.open} merchant={storeLive.merchant} onClose={() => setStoreLive({ open: false })} onChanged={() => setRefresh((n) => n + 1)} />
    </>
  );
}
